// src/pages/AdminSettings.jsx
// 管理員設定頁面 - 編輯資料、修改密碼、登出

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Lock, LogOut, Save, X, 
  AlertCircle, CheckCircle, ArrowLeft 
} from 'lucide-react';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // profile, password
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // 管理員資料
  const [profileData, setProfileData] = useState({
    id: '',
    username: '',
    name: '',
    email: '',
    role: ''
  });
  
  // 修改密碼表單
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    loadAdminInfo();
  }, []);

  const loadAdminInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (result.error) {
        throw new Error(result.message);
      }

      setProfileData(result.data);
    } catch (err) {
      setError('載入資料失敗：' + err.message);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (!profileData.name || !profileData.email) {
      setError('請填寫所有必填欄位');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: profileData.name,
          email: profileData.email
        })
      });

      const result = await response.json();

      if (result.error) {
        throw new Error(result.message);
      }

      // 更新 localStorage
      localStorage.setItem('adminName', result.data.name);

      setSuccess('資料更新成功！');
      loadAdminInfo();
      
    } catch (err) {
      setError(err.message || '更新失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password) {
      setError('請填寫所有密碼欄位');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('新密碼與確認密碼不一致');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setError('新密碼長度至少需要 6 個字元');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          old_password: passwordData.old_password,
          new_password: passwordData.new_password
        })
      });

      const result = await response.json();

      if (result.error) {
        throw new Error(result.message);
      }

      setSuccess('密碼修改成功！');
      setPasswordData({
        old_password: '',
        new_password: '',
        confirm_password: ''
      });
      
    } catch (err) {
      setError(err.message || '修改密碼失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    if (!window.confirm('確定要登出嗎？')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // 調用登出 API（可選，因為 JWT 是無狀態的）
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('登出 API 調用失敗:', err);
    } finally {
      // 清除 localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('adminRole');
      localStorage.removeItem('adminName');
      localStorage.removeItem('adminUsername');
      localStorage.removeItem('adminEmail');
      
      // 跳轉到登入頁
      navigate('/admin/login');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">帳號設定</h1>
                <p className="text-sm text-gray-600 mt-1">管理您的個人資料和安全設定</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 訊息提示 */}
        {success && (
          <div className="mb-6 flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {error && (
          <div className="mb-6 flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Tab 切換 */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex items-center gap-2 px-6 py-4 font-medium border-b-2 transition-colors ${
                  activeTab === 'profile'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <User className="w-5 h-5" />
                個人資料
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`flex items-center gap-2 px-6 py-4 font-medium border-b-2 transition-colors ${
                  activeTab === 'password'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Lock className="w-5 h-5" />
                修改密碼
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* 個人資料表單 */}
            {activeTab === 'profile' && (
              <form onSubmit={handleUpdateProfile} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* 帳號（唯讀） */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      帳號
                    </label>
                    <input
                      type="text"
                      value={profileData.username}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">帳號無法修改</p>
                  </div>

                  {/* 角色（唯讀） */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      角色
                    </label>
                    <input
                      type="text"
                      value={profileData.role === 'super_admin' ? '超級管理員' : '管理員'}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    />
                  </div>

                  {/* 姓名 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={profileData.name}
                      onChange={handleProfileChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                {/* 按鈕 */}
                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center gap-2 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Save className="w-5 h-5" />
                    {loading ? '儲存中...' : '儲存變更'}
                  </button>
                  <button
                    type="button"
                    onClick={loadAdminInfo}
                    className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <X className="w-5 h-5" />
                    取消
                  </button>
                </div>
              </form>
            )}

            {/* 修改密碼表單 */}
            {activeTab === 'password' && (
              <form onSubmit={handleChangePassword} className="space-y-6">
                <div className="max-w-md space-y-4">
                  {/* 舊密碼 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      舊密碼 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="old_password"
                      value={passwordData.old_password}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* 新密碼 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      新密碼 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                      minLength={6}
                    />
                    <p className="mt-1 text-xs text-gray-500">至少 6 個字元</p>
                  </div>

                  {/* 確認新密碼 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      確認新密碼 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                {/* 按鈕 */}
                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center gap-2 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Lock className="w-5 h-5" />
                    {loading ? '修改中...' : '修改密碼'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPasswordData({
                      old_password: '',
                      new_password: '',
                      confirm_password: ''
                    })}
                    className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <X className="w-5 h-5" />
                    清除
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* 登出區域 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">登出帳號</h3>
          <p className="text-gray-600 mb-4">
            登出後需要重新輸入帳號密碼才能訪問後台
          </p>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <LogOut className="w-5 h-5" />
            登出
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;